import { useEffect, useState } from "react";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import useData from "src/hooks/useApi";
import useMessage from "src/hooks/useMessage";
import {
    mainContentContainer,
    pageHeaderStyle,
} from "src/components/SharedStyles";

interface ConnectedApp {
    clientId: string;
    clientName: string;
    scopes: string[];
    grantedAt: number;
}

interface ScopeMeta {
    group: string;
    groupLabel: string;
    label: string;
    description: string;
}

// `grantedAt` is a Unix timestamp in seconds, so convert to milliseconds for Date().
function formatDate(seconds: number | null): string {
    if (!seconds) return "Never";
    return new Date(Number(seconds) * 1000).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
}

export default function ConnectedAppsPage() {
    const api = useData();
    const { showSuccess, showError } = useMessage();

    const [apps, setApps] = useState<ConnectedApp[]>([]);
    const [scopeMeta, setScopeMeta] = useState<Record<string, ScopeMeta>>({});
    const [appsLoading, setAppsLoading] = useState(false);
    const [revokingApp, setRevokingApp] = useState<string | null>(null);

    async function loadApps() {
        setAppsLoading(true);
        try {
            const data = await api?.fetchData("/oauth/connected-apps");
            setApps(Array.isArray(data) ? data : []);
        } catch {
            showError("Failed to load connected apps");
        } finally {
            setAppsLoading(false);
        }
    }

    async function loadScopeMeta() {
        try {
            const data = await api?.fetchData("/oauth/scopes");
            setScopeMeta(data ?? {});
        } catch {
            // Non-fatal: chips fall back to the raw scope string.
        }
    }

    useEffect(() => {
        loadApps();
        loadScopeMeta();
    }, []);

    async function revokeApp(clientId: string) {
        setRevokingApp(clientId);
        try {
            await api?.deleteData(`/oauth/connected-apps/${clientId}`);
            showSuccess("Access revoked");
            setApps((prev) => prev.filter((a) => a.clientId !== clientId));
        } catch {
            showError("Failed to revoke access");
        } finally {
            setRevokingApp(null);
        }
    }

    return (
        <Box sx={mainContentContainer}>
            <Grid container rowSpacing={3} columnSpacing={2}>
                {/* ── Page header ───────────────────────────────────────── */}
                <Grid item xs={12}>
                    <Typography sx={pageHeaderStyle}>Connected Apps</Typography>
                    <Typography sx={subHeading}>
                        Applications you have granted access to your
                        AutoBackorder account via OAuth. Revoking access
                        immediately invalidates their tokens.
                    </Typography>
                </Grid>

                {/* ── Connected OAuth Apps ──────────────────────────────── */}
                <Grid item xs={12}>
                    {appsLoading ? (
                        <Box sx={centeredBox}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : apps.length === 0 ? (
                        <Box sx={emptyBox}>
                            <LinkOffIcon
                                sx={{ color: "#bbb", fontSize: 36, mb: 1 }}
                            />
                            <Typography sx={emptyText}>
                                No connected apps yet.
                            </Typography>
                        </Box>
                    ) : (
                        <TableContainer sx={tableContainer}>
                            <Table sx={{ minWidth: 720 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={headCell}>
                                            Application
                                        </TableCell>
                                        <TableCell sx={headCell}>
                                            Connected
                                        </TableCell>
                                        <TableCell sx={headCell}>
                                            Permissions
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                ...headCell,
                                                textAlign: "right",
                                            }}
                                        >
                                            Action
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {apps.map((app) => (
                                        <TableRow key={app.clientId} hover>
                                            <TableCell sx={bodyCell}>
                                                <Typography sx={appName}>
                                                    {app.clientName ||
                                                        app.clientId}
                                                </Typography>
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    ...bodyCell,
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                <Typography sx={dateText}>
                                                    {formatDate(app.grantedAt)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={bodyCell}>
                                                <Box sx={permList}>
                                                    {app.scopes.map((s) => {
                                                        const meta =
                                                            scopeMeta[s];
                                                        return (
                                                            <Box
                                                                key={s}
                                                                sx={scopeItem}
                                                            >
                                                                <Chip
                                                                    label={
                                                                        meta?.groupLabel ??
                                                                        s
                                                                    }
                                                                    size="small"
                                                                    sx={
                                                                        scopeChip
                                                                    }
                                                                />
                                                                <Typography
                                                                    sx={
                                                                        scopeDescText
                                                                    }
                                                                >
                                                                    {meta?.description ??
                                                                        s}
                                                                </Typography>
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    ...bodyCell,
                                                    textAlign: "right",
                                                }}
                                            >
                                                <Button
                                                    onClick={() =>
                                                        revokeApp(app.clientId)
                                                    }
                                                    disabled={
                                                        revokingApp ===
                                                        app.clientId
                                                    }
                                                    variant="contained"
                                                    startIcon={
                                                        revokingApp ===
                                                        app.clientId ? (
                                                            <CircularProgress
                                                                size={16}
                                                                sx={{
                                                                    color: "#fff",
                                                                }}
                                                            />
                                                        ) : (
                                                            <LinkOffIcon />
                                                        )
                                                    }
                                                    sx={revokeBtn}
                                                >
                                                    Revoke
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const subHeading = { fontSize: "14px", color: "#777", mb: 2 };
const tableContainer = {
    border: "1px solid #eee",
    borderRadius: 2,
    bgcolor: "#fff",
};
const headCell = {
    fontSize: "11px",
    fontWeight: 700,
    color: "#888",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    bgcolor: "#fafafa",
    borderBottom: "1px solid #eee",
    py: 1.5,
};
const bodyCell = {
    verticalAlign: "top",
    py: 2,
    borderBottom: "1px solid #f3f3f3",
};
const appName = { fontSize: "14px", fontWeight: 600, color: "#333" };
const dateText = { fontSize: "13px", color: "#555" };
const permList = { display: "flex", flexDirection: "column", gap: 0.75 };
const revokeBtn = {
    bgcolor: "#e53935",
    color: "#fff",
    textTransform: "none",
    fontWeight: 600,
    fontSize: "13px",
    borderRadius: "8px",
    boxShadow: "none",
    px: 2,
    "&:hover": { bgcolor: "#c62828", boxShadow: "none" },
    "&.Mui-disabled": { bgcolor: "#e5393588", color: "#fff" },
};
const scopeItem = {
    display: "flex",
    alignItems: "center",
    gap: 1,
};
const scopeChip = {
    bgcolor: "#f5f0c0",
    color: "#555",
    fontSize: "11px",
    height: 20,
    flexShrink: 0,
    minWidth: 78,
};
const scopeDescText = { fontSize: "12px", color: "#666", lineHeight: 1.4 };
const emptyBox = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    py: 4,
    color: "#bbb",
};
const emptyText = { fontSize: "14px", color: "#bbb" };
const centeredBox = { display: "flex", justifyContent: "center", py: 4 };
